import dayjs from 'dayjs';
import { formatToCurrency } from '@/lib/helpers';

/**
 * Generate the Bill of Sales document
 *
 * @param {Object=} data - The data to be used to generate the document
 */
export const generateBillOfSales = (data = {}) => {
  const borderColor = '#9ca3af';
  const bigMargin = 20;
  const smallMargin = 10;

  const hr = (width = 1, lineHeight = 0.25) => ({
    table: {
      widths: ['*'],
      body: [[' '], [' ']]
    },
    lineHeight,
    layout: {
      hLineWidth: (i, node) =>
        i === 0 || i === node.table.body.length ? 0 : width,
      vLineWidth: () => 0
    }
  });

  const tableLayout = {
    hLineColor: borderColor,
    vLineColor: borderColor,
    paddingBottom: () => 4,
    paddingTop: () => 4,
    paddingLeft: () => 8,
    paddingRight: () => 8
  };

  const tableBoxLayout = {
    hLineColor: borderColor,
    vLineColor: borderColor,
    hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 1 : 0),
    vLineWidth: (i, node) =>
      i === 0 || i === node.table.widths.length ? 1 : 0,
    paddingBottom: () => 4,
    paddingTop: () => 4,
    paddingLeft: () => 8,
    paddingRight: () => 8
  };

  const branchName = data.user?.branch?.branchName || '';
  const branchAddress = `${data?.user?.branch?.branchStreet} ${data?.user?.branch?.branchCity} ${data?.user?.branch?.branchProvince} ${data?.user?.branch?.branchCountry}`;
  // Get the main branch phone number
  const branchPhones = () => {
    if (!data?.user?.branch?.branchTelephone) return '';
    const mainBranch = data?.user?.branch?.branchTelephone.find(
      (t) => t.isMain == true
    );
    if (!mainBranch) return '';
    return mainBranch.number;
  };
  const accountHolders = () => {
    if (!data.loan?.loanDistributions) return '';
    return data.loan?.loanDistributions
      .map(
        (d) =>
          `${d.person?.firstName} ${d.person?.middleName} ${d.person?.lastName}`
      )
      .join(', ');
  };
  /**@type {import('pdfmake/interfaces').TDocumentDefinitions} */
  const dd = {
    footer: function (currentPage, pageCount) {
      return {
        text: `Page ${currentPage.toString()} of ${pageCount.toString()}`,
        alignment: 'center'
      };
    },
    info: {
      title: 'Bill Of Sales', // name of the document being printed
      modDate: new Date(), // date the document was last modified
      creationDate: new Date(), // date the document was created
      keywords: 'bill of sales, loan, finance', // keywords associated with the document
      author: data.company?.name, // company name
      subject: 'Bill Of Sales', // name of the document being printed
      creator: data.user?.username, // name of the logged in user
      producer: data.company?.name // company name
    },
    content: [
      data.company?.companyImage && {
        image: 'companyImage',
        alignment: 'center',
        fit: [80, 80]
      },
      {
        marginTop: 20,
        text: data.company?.name,
        alignment: 'center',
        fontSize: 22,
        bold: true
      },
      {
        marginBottom: bigMargin,
        columns: [
          ' ',
          {
            text: 'Bill Of Sale',
            alignment: 'center',
            fontSize: 14,
            marginBottom: 10,
            bold: true
          },
          {
            text: 'A/C# 1234567890',
            alignment: 'right'
          }
        ]
      },
      {
        text: `THIS INDENTURE is made on the date set out in ITEM 1 of the First Schedule hereto BETWEEN the
party whose name and address is set out in ITEM 2 and 3 of the First Schedule hereto (hereinafter referred to as "the BORROWER") of the ONE PART, the party whose name and address is set out at ITEM 4 and 5 respectively of the First Schedule (hereafter referred to as "the GUARANTOR") of the SECOND PART AND ${data.company?.name} a Co-operative Society duly incorporated under the Laws of Jamaica and having its registered office at 66 Slipe Road, Kingston 5, in the parish of St. Andrew (hereinafter referred to as "the CREDIT UNION" which expression shall, where the context admits, include its successors and assigns) of the THIRD PART.`,
        marginBottom: bigMargin / 2
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: 'WHEREAS', width: 70 },
          {
            text: 'The BORROWER and/or GUARANTOR is the absolute owner in possession free from encumbrances of the chattels and things more particularly mentioned and described in the Second Schedule hereto (hereinafter referred to as “the CHATTEL”).'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: 'WHEREAS', width: 70 },
          {
            text: 'The BORROWER has applied to the CREDIT UNION for a loan of the sum set out in ITEM 6 of the First Schedule (hereinafter referred to as “the LOAN AMOUNT”) hereto and the CREDIT UNION has consented to grant same. The repayment thereof is to be made with interest and secured in the manner as described herein and upon the terms and conditions herein contained and implied.'
          }
        ]
      },
      { text: 'NOW THIS INDENTURE WITNESSETH:', marginBottom: bigMargin / 2 },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '1.', width: 70 },
          {
            text: 'In consideration of the CREDIT UNION making or continuing advances or otherwise giving credit or affording banking facilities, for so long as the CREDIT UNION may think fit to the BORROWER, the GUARANTOR as BENEFICIAL OWNER of the CHATTEL HEREBY ASSIGNS, TRANSFERS and SETS OVER THE SAID CHATTELS UNTO the CREDIT UNION absolutely, subject to the proviso for entry of satisfaction hereinafter contained.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.', width: 70 },
          {
            text: 'For the consideration aforesaid, the BORROWER and/or GUARANTOR for himself, his executors, heirs, legal representatives and administrators HEREBY COVENANTS and agrees with the CREDIT UNION, its successors and assigns as follows:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1', width: 70 },
          { text: 'PAYMENTS:', decoration: 'underline' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.1', width: 70 },
          {
            text: 'All payments to be made under this agreement shall be made:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.1.1', width: 70 },
          {
            text: 'at the Head Office of the CREDIT UNION at 66 Slipe Road, Kingston 5, in the parish of St Andrew or such other branch of the CREDIT UNION (hereinafter referred to as “The Location”); or'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.1.2', width: 70 },
          { text: 'to any account provided by the CREDIT UNION.' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.2', width: 70 },
          {
            text: 'To pay to the CREDIT UNION on demand at the Location all sums of money which:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.2.1', width: 70 },
          {
            text: 'now or at any time hereafter may be due or owing by the BORROWER to the CREDIT UNION; or'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.2.2', width: 70 },
          {
            text: 'the GUARANTOR may be or become liable to the CREDIT UNION on any current or other account or in any other manner.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.3', width: 70 },
          {
            text: 'To discharge all liabilities incurred by the BORROWER to the CREDIT UNION in any manner whatever (and in the case of both money owing and liabilities incurred whether alone or jointly with any other person and in whatever style or name and whether such monies are owed or liabilities are incurred as principal or surety for the payment of the indebtedness to the CREDIT UNION of any person, limited company or other corporation, firm, committee, association or other unincorporated body whatsoever).'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.4', width: 70 },
          {
            text: 'The GUARANTOR shall pay interest on all sums outstanding of the LOAN AMOUNT including, but not limited to commission, discount and other banking charges, legal and other costs, charges and expenses:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.4.1', width: 70 },
          {
            text: 'from the date due until the date paid at the rate stipulated at ITEM 7 of the First Schedule or at such other rate or rates of interest as the CREDIT UNION shall from time to time charge; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.4.2', width: 70 },
          {
            text: 'computed daily at such rate or rates as aforesaid which will be applicable after and before any judgment for the recovery of the moneys due or owing or for which the BORROWER may become liable to the CREDIT UNION as aforesaid or any part thereof.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.5', width: 70 },
          {
            text: 'All sums to be paid in accordance with Section 2.1.4 shall be paid in installments as follows:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.5.1', width: 70 },
          {
            text: 'the amount to be paid for each installment is the amount stipulated at ITEM 8 of the First Schedule; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.5.2', width: 70 },
          {
            text: 'the number of installments for which payment is to be made is stipulated at ITEM 9 of the First Schedule; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.5.3', width: 70 },
          {
            text: 'the date in each month that payment is due for each installment shall be the date stipulated at ITEM 10 of the First Schedule.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.6', width: 70 },
          {
            text: 'Section 2.1.5 shall not prevent the CREDIT UNION’s ability at its sole discretion and from time to time, to:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.6.1', width: 70 },
          {
            text: 'vary the rate stipulated in ITEM 7, or the amount stipulated at ITEM 8, or the number of installments stipulated at ITEM 9, or the installment due date stipulated at ITEM 10 respectively of the First Schedule; or'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.6.2', width: 70 },
          {
            text: 'require payment for any sum advanced or paid on behalf of the borrower for any reason in accordance with this agreement; or'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.1.6.3', width: 70 },
          {
            text: 'require payment of any penalties or interest or other expenses.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.2', width: 70 },
          { text: 'PENALTY', decoration: 'underline' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.2.1', width: 70 },
          {
            text: 'Any sums mentioned in CLAUSE 2.1. of this agreement which are not paid punctually for two or more months (consecutive or combined) will attract a penalty of Two Percent (2%) per month on the total arrears beginning the second month and each month thereafter until all such arrears have been paid but without prejudice to the right of the CREDIT UNION to require payment of such interest.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3', width: 70 },
          {
            text: 'DUTY TO INSURE',
            decoration: 'underline'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.1', width: 70 },
          {
            text: 'To insure and keep insured the CHATTEL:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.1.1', width: 70 },
          {
            text: 'as required by Law; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.1.2', width: 70 },
          {
            text: 'against fire, theft and damage and such other contingencies and risks as the CREDIT UNION shall reasonable require; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.1.3', width: 70 },
          {
            text: 'in the name of the CREDIT UNION; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.1.4', width: 70 },
          {
            text: 'to the full insurable value thereof or such other amount as may reasonably be required by the CREDIT UNION; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.1.5', width: 70 },
          {
            text: 'in some insurance office to be approved by the CREDIT UNION.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.2', width: 70 },
          {
            text: 'To punctually make all payments required for keeping such insurances on foot and to produce and deliver up to the CREDIT UNION the said policy of insurance and the receipt for each payment of premium paid in respect thereof; and if the BORROWER shall default at any time in effecting and keeping up such insurance:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.2.1', width: 70 },
          {
            text: 'it shall be lawful for the CREDIT UNION to insure and keep insured the CHATTEL as aforesaid; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.2.2', width: 70 },
          {
            text: 'The BORROWER and/or GUARANTOR shall repay all money expended by the CREDIT UNION for such purpose together with interest thereon in accordance with the rate stipulated at ITEM 7 of the First Schedule.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.3', width: 70 },
          {
            text: 'Until the monies aforementioned at Clause 2.3.2.2 are repaid in full, it shall be a charge upon the CHATTEL hereby assigned.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.4', width: 70 },
          {
            text: 'THE BORROWER and/or GUARANTOR HEREBY IRREVOCABLY APPOINTS the CREDIT UNION to be the Attorney for himself; the BORROWER, his representatives, his executors and administrators during the subsistence of this Indenture to:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.4.1', width: 70 },
          {
            text: 'ask for, demand, sue for, recover and receive and give effectual discharges for all moneys which may become due and owing or payable to, under or in respect of any insurance now or hereafter to be effected on the CHATTEL or any of them or any CHATTELs and things substituted therefore; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.4.2', width: 70 },
          {
            text: 'without prejudice to the rights conferred upon the CREDIT UNION hereunder or by Law, all such moneys shall, if the CREDIT UNION so requires, be applied in or towards repayment of any moneys due or owing to the CREDIT UNION and hereby secured.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.3.5', width: 70 },
          {
            text: 'The BORROWER and/or GUARANTOR and every other person claiming by or through the GUARANTOR any interest in the CHATTEL will at all times at the sole cost of the GUARANTOR execute and do all such assurances and things for the future and better assigning of the CHATTEL unto the CREDIT UNION and enabling the CREDIT UNION to obtain possession of the same as may be required by the CREDIT UNION.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.4', width: 70 },
          {
            text: 'DUTY TO MAINTAIN CHATTEL',
            decoration: 'underline'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.4.1', width: 70 },
          {
            text: 'The BORROWER and/or GUARANTOR shall:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.4.1.1', width: 70 },
          {
            text: 'keep the CHATTEL in good repair and running order and complete with all fittings and accessories with which the same were purchased.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.4.1.2', width: 70 },
          {
            text: 'neither permit nor allow the CHATTEL or any part or parts thereof to be injured, malfunctioned or deteriorated subsequent to the execution of this bill of sale in a greater degree than the same would deteriorate by reasonable use and wear thereof.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.4.1.2', width: 70 },
          {
            text: 'replace, repair and make good any part or parts of the CHATTEL which are or may become injured, damaged, stolen, malfunctioned or deteriorated forthwith.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.4.2', width: 70 },
          {
            text: 'Any part(s) which for any reason has been substituted, repaired, or replaced for another shall be deemed to be included in this security.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.5', width: 70 },
          {
            text: 'RIGHT TO INSPECT OR TAKE INVENTORIES',
            decoration: 'underline'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.5.1', width: 70 },
          {
            text: 'The BORROWER and/or GUARANTOR shall, as may be demanded by the CREDIT UNION from time to time:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.5.1.1', width: 70 },
          {
            text: 'permit the entry of the CREDIT UNION by its servants or agents to enter any premises or building in which the CHATTEL may be located for the time being and to view and inspect and take inventories thereof; or'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.5.1.2', width: 70 },
          {
            text: 'make the CHATTEL available for viewing; inspection or the taking of inventories thereof by the CREDIT UNION at a location specified by the CREDIT UNION.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.6', width: 70 },
          { text: 'SEIZURE', decoration: 'underline' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.6.1', width: 70 },
          {
            text: 'Not to permit or allow any writ of fieri facias or other writ, execution or distress for rent rates or taxes to be levied or taken or distrained upon the CHATTEL.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.6.2', width: 70 },
          {
            text: 'The CREDIT UNION, its servants or agents may, without previous notice to the BORROWER and/or GUARANTOR, seize and take possession of the CHATTEL in whatever place or places they may happen to be. The aforesaid right shall apply so long as any money shall be owing on the Chattel or any securities of these presents and notwithstanding that the same may not as yet have become payable.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.6.3', width: 70 },
          {
            text: 'That in exercise of the power to seize the CHATTEL assigned, the CREDIT UNION, its servants and agents may enter and remain upon any premises where the CHATTEL may be or is believed to be and if necessary may break open doors and windows, gates or fences in order to obtain possession thereof and to seize and take away the same and, after the expiration of seven clear days from the date of the seizure, may sell the CHATTEL either separately or in lots by public auction or private contract on or off the said premises and retain out of the proceeds the balance of the moneys hereby secured and interest as may then remain unpaid and all costs and expenses incurred, made or sustained in or about entering upon the said premises and in discharging any distress, execution or other encumbrances on the CHATTEL and seizing, taking, retaining and keeping possession of the CHATTEL and in and about the removal or sale (including the costs of advertising) of the CHATTEL and from and after the full payment of all moneys then due or owing to the CREDIT UNION and such costs, charges, payments, expenses and encumbrances as aforesaid shall pay over the surplus (if any) of the proceeds of such sale to the GUARANTOR.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.7', width: 70 },
          { text: 'NO WAIVER', decoration: 'underline' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.7.1', width: 70 },
          {
            text: 'That no neglect, waiver, omission or forbearance on the part of the CREDIT UNION to take advantage of or enforce any right or remedy arising out of any breach or non-observance of any covenant or condition herein contained or implied shall be deemed to be or operate as a general waiver of such covenant or condition or the right to enforce or take advantage of the same in respect of any breach or non-observance thereof either original or recurring.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.8', width: 70 },
          {
            text: [
              { text: 'ADVANCES', decoration: 'underline' },
              {
                text: '\nIt shall be lawful for but not obligatory on the CREDIT UNION to advance and pay all moneys necessary for remedying any breach or non-observance by the BORROWER and/or GUARANTOR of any of the covenants or stipulations hereunder and all costs, charges and expenses properly incurred hereunder by the CREDIT UNION and all other moneys properly in time or respective times of paying or advancing the same and shall be repayable on demand. Further, such amounts incurred by the CREDIT UNION may be recovered from any payments made towards the LOAN AMOUNT.'
              }
            ]
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.8.1', width: 70 },
          {
            text: 'This security shall be in addition and without prejudice to any and every other remedy, lien or security which the CREDIT UNION has the right to recover.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.9', width: 70 },
          { text: 'STAMP DUTY', decoration: 'underline' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.9.1', width: 70 },
          {
            text: 'That the total amount of the money secured or to be ultimately recoverable hereunder begin uncertain and without any limit this indenture shall be impressed in the first instance with stamp duty to cover the LOAN AMOUNT.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.9.2', width: 70 },
          {
            text: 'The CREDIT UNION shall be and is hereby empowered at any time or times hereafter (without further license or consent of the BORROWER and/or GUARANTOR) to impress additional stamp duty hereon covering any sum or sums by which the said indebtedness may exceed the LOAN AMOUNT hereto it being the intent of these presents that until its discharge the Bill of Sale hereby created shall be a continuing security covering the indebtedness of the BORROWER to the CREDIT UNION for such amount of money as the stamp duty impressed hereon will extend to cover'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.10', width: 70 },
          {
            text: 'NO OBLIGATION TO LEND, IMMEDIATE DISCHARGE',
            decoration: 'underline'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.10.1', width: 70 },
          {
            text: 'The CREDIT UNION:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.10.1.1', width: 70 },
          {
            text: 'shall not be under any obligation to afford or continue credit or facilities to the BORROWER to any aggregate sum in excess of such limit and extent as the CREDIT UNION may, in its absolute discretion from time to time think fit; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.10.1.2', width: 70 },
          {
            text: "the CREDIT UNION may at any time or times require the reduction or discharge of the BORROWER's indebtedness secured by this Bill of Sale."
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.10.2', width: 70 },
          {
            text: 'The GUARANTOR shall be bound to comply immediately with every such requisition or demand aforementioned in Clause 2.10.1.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.11', width: 70 },
          { text: 'INDEMNITY', decoration: 'underline' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.11.1', width: 70 },
          {
            text: 'That the CREDIT UNION shall not be liable for any involuntary loss which may occur by reason of the exercise or execution (whether contemporaneously or otherwise) of any or all of its rights, remedies and powers conferred, given or implied by this indenture or by Law'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.12', width: 70 },
          {
            text: 'RIGHT TO VARY TIME',
            decoration: 'underline'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.12.1', width: 70 },
          {
            text: 'That without prejudice to any powers hereinbefore contained, the CREDIT UNION shall from time to time and at its sole discretion have the power to vary:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.12.1.1', width: 70 },
          {
            text: 'the rate of interest then payable hereunder whether by way of increase or reduction; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.12.1.2', width: 70 },
          {
            text: 'the dates on and the periods for which interest shall attract a penalty; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.12.1.3', width: 70 },
          {
            text: 'The dates on which payment of any bill or bills of exchange, promissory note or other security or securities is due without in any manner releasing the GUARANTOR or affecting the security hereby made.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.12.2', width: 70 },
          {
            text: 'The CREDIT UNION shall not be required to give any prior notice whatsoever to the BORROWER or GUARANTOR of any variation done in accordance with Clause 2.12 of this agreement.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.13', width: 70 },
          { text: 'CONTINUING SECURITY', decoration: 'underline' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.13.1', width: 70 },
          { text: 'This security shall:' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.13.1.1', width: 70 },
          {
            text: 'not be considered as satisfied or discharged by any intermediate payment of the whole or part of the moneys owing as aforesaid; but'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.13.1.2', width: 70 },
          {
            text: 'constitute and be a continuing security to the CREDIT UNION notwithstanding any settlement of account or other matter or thing whatsoever; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.13.1.3', width: 70 },
          {
            text: 'be in addition to any other security or- securities which the CREDIT UNION may from time to time hold or take from the BORROWER or GUARANTOR; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.13.1.4', width: 70 },
          {
            text: 'not be affected or prejudiced by or affect or prejudice in any way any other security or securities which the CREDIT UNION may now or hereafter hold or take from the BORROWER or GUARANTOR.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.14', width: 70 },
          { text: 'NOTICE', decoration: 'underline' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.14.1', width: 70 },
          {
            text: 'A demand for payment or any other demand or notice hereunder may be properly and effectually made by:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.14.1.1', width: 70 },
          {
            text: 'any Manager or Assistant Manager of any branch of the CREDIT UNION or by an agent, attorney or Attorney-at-Law of or appointed by the CREDIT UNION; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.14.1.2', width: 70 },
          {
            text: 'letter sent by registered post, addressed to the BORROWER or GUARANTOR at the address given in this security or at the last known place of business of the BORROWER or GUARANTOR.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.14.2', width: 70 },
          {
            text: 'Every demand made in accordance with Clause 2.14.1 shall be deemed to have been made on the third day following the day the letter was posted in any post office in Jamaica.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.15', width: 70 },
          {
            text: 'SATISFACTION OF LOAN',
            decoration: 'underline'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.15.1', width: 70 },
          {
            text: 'Satisfaction may at the discretion of the CREDIT UNION be entered into the margin of the record of these presents after payment by the BORROWER or GUARANTOR to the CREDIT UNION of:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.15.1.1', width: 70 },
          { text: 'all moneys payable under this security; and' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [{ text: '2.15.1.2', width: 70 }, { text: 'all costs; and' }]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.15.1.3', width: 70 },
          {
            text: 'all expenses which it may incur in lawfully seizing and removing the CHATTEL hereby assigned; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.15.1.4', width: 70 },
          {
            text: 'any costs which it may in any wise incur in or about the protection, enforcement or realization of this security'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.16', width: 70 },
          { text: 'AUTHORITY TO COMPLETE', decoration: 'underline' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.16.1', width: 70 },
          {
            text: 'The GUARANTOR HEREBY AUTHORIZES and EMPOWERS the CREDIT UNION to fill out and complete any documents:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.16.1.1', width: 70 },
          {
            text: 'signed by the BORROWER and delivered to the CREDIT UNION; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.16.1.2', width: 70 },
          {
            text: 'which may be necessary to be filled out and completed in order to effectually transfer the CHATTEL to a purchaser in the event of the CREDIT UNION exercising its powers of sale under these presents'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.17', width: 70 },
          { text: 'NO WARRANTY AS TO THE CONDITION:', decoration: 'underline' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.17.1', width: 70 },
          {
            text: 'The BORROWER and/or GUARANTOR is deemed to have examined the CHATTEL prior to the sale and no warranty, condition, description or representation on the part of the CREDIT UNION as to the Merchantable quality or fitness for the purpose is neither given; nor implied; nor is any warranty condition, description or representation is to be taken or to have been given or implied from anything said or written and any statutory or other warranty of condition or of description expressed or implied as to the CHATTEL, is hereby expressly excluded. The BORROWER and/or GUARANTOR must take them of his own judgement as the result of its examination and no description expressed or implied given by the CREDIT UNION shall constitute a sale by description'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.18', width: 70 },
          { text: 'DATA PROTECTION', decoration: 'underline' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.18.1', width: 70 },
          {
            text: 'As far as is reasonably required, for the service being offered by the CREDIT UNION in regards to the execution and performance of this Agreement and its securities: both the BORROWER and GUARANTOR hereby consent to the collection, use, disclosure and processing of personal data and information provided by BORROWER and the GUARANTOR to the CREDIT UNION or otherwise obtained.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.18.2', width: 70 },
          {
            text: 'Personal data and information reasonably required, may be used by, disclosed to or shared with the following:'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.18.2.1', width: 70 },
          {
            text: "Senior Management and regular employees of the CREDIT UNION's various departments and branches for maintaining of accurate records and financial statements;"
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.18.2.2', width: 70 },
          {
            text: 'Local Authorities under statutory legislation such as the Credit Reporting Act;'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.18.2.3', width: 70 },
          {
            text: "debt collectors pursuant to CREDIT UNION's rights of seizure;"
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.18.2.4', width: 70 },
          {
            text: 'the Court and legal advisors pursuant to any legal action to be taken in regards to these presents;'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.18.2.5', width: 70 },
          {
            text: 'Any other individual in the context of execution and performance of this Agreement and its securities.'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.19', width: 70 },
          { text: 'CREDIT REPORTING', decoration: 'underline' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.19.1', width: 70 },
          { text: 'The GUARANTOR hereby consents:' }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.19.1.1', width: 70 },
          {
            text: 'To Credit Bureaus licensed under the Credit Reporting Act disclosing credit information about the GUARANTOR to the CREDIT UNION; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.19.1.2', width: 70 },
          {
            text: 'To the CREDIT UNION disclosing credit information about the GUARANTOR to Credit Bureaus licensed under the Credit Reporting Act; and'
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.19.1.3', width: 70 },
          {
            text: 'To the CREDIT UNION providing a signed copy of this agreement or any securities or otherwise to any Credit Bureaus licensed under the Credit Reporting Act by electronic or any other means.'
          }
        ]
      },
      {
        pageBreak: 'before',
        marginBottom: bigMargin,
        text: 'IN WITNESS WHEREOF the GUARANTOR has hereunto set his hand the day and year set out in ITEM 1 of the First Schedule hereto.'
      },
      {
        alignment: 'center',
        bold: true,
        text: `A/C #: ${data.loan?.accountNumber}`
      },
      {
        alignment: 'center',
        bold: true,
        text: `FIRST SCHEDULE`,
        decoration: 'underline',
        marginBottom: bigMargin / 2
      },
      {
        layout: tableLayout,
        marginTop: bigMargin,
        marginBottom: bigMargin,
        table: {
          widths: [50, 150, '*'],
          body: [
            [
              'ITEM 1',
              { text: 'Date of Indenture :', alignment: 'right' },
              '24th June 2024'
            ],
            [
              'ITEM 2',
              { text: 'Name of BORROWER :', alignment: 'right' },
              {
                text: [{ text: 'MARIA R HALL\n', bold: true }, `TRN: 374059582`]
              }
            ],
            [
              'ITEM 3',
              { text: 'Address of BORROWER :', alignment: 'right' },
              'CROSS ROAD , KINGSTON , KINGSTON'
            ],
            [
              'ITEM 4',
              { text: 'Name of GUARANTOR(1) :', alignment: 'right' },
              { text: 'N/A', bold: true }
            ],
            [
              'ITEM 5',
              { text: 'Address of GUARANTOR(1) :', alignment: 'right' },
              { text: 'N/A', bold: true }
            ],
            [
              'ITEM 6',
              { text: 'LOAN AMOUNT', alignment: 'right' },
              { text: 'THREE MILLION DOLLARS ($3,000,000.00)', bold: true }
            ],
            [
              'ITEM 7',
              { text: 'Rate of interest on amounts :', alignment: 'right' },
              { text: '7.50% per annum on the addon balance' }
            ],
            [
              'ITEM 8',
              { text: 'Amount of Each Instalment :', alignment: 'right' },
              { text: '$10,096.15 WEEKLY' }
            ],
            [
              'ITEM 9',
              { text: 'No. of Installments :', alignment: 'right' },
              { text: '520' }
            ],
            [
              'ITEM 10',
              { text: 'Each instalment is due :', alignment: 'right' },
              { text: 'on or before the 30th day of each month.' }
            ]
          ]
        }
      },
      {
        alignment: 'center',
        bold: true,
        text: `SECOND SCHEDULE`,
        decoration: 'underline',
        marginBottom: bigMargin / 4
      },
      {
        alignment: 'center',
        bold: true,
        text: `(List of Chattels and things herein before referred to)`,
        decoration: 'underline',
        marginBottom: bigMargin / 4
      },
      {
        alignment: 'center',
        bold: true,
        text: `SECOND SCHEDULE CHATTEL`,
        decoration: 'underline'
      },
      {
        pageBreak: 'before',
        text: `I James Brow of Lot 41, Rolli Street, Kingston, Jamaica in the Parish of Kingston being duly sworn make oath and say as follows:`,
        marginBottom: bigMargin / 2
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '1.', width: 50 },
          {
            text: [
              `That within Indenture made Between of the One Part and COK SODALITY CO-OPERATIVE CREDIT UNION LIMITED, of the Other Part being an assignment of personal property by the said was made and delivered on the day it bears date namely the `,
              { text: '_______', font: 'Roboto' },
              ` day of `,
              { text: '____________________', font: 'Roboto' },
              '20',
              { text: '____', font: 'Roboto' }
            ]
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '2.', width: 50 },
          {
            text: [
              `That I was present and saw the said whose name(s) is/are subscribed in the said Indenture sign, seal and as and for his/her/their proper act and deed deliver the same on the `,
              { text: '_______', font: 'Roboto' },
              ` day of `,
              { text: '____________________', font: 'Roboto' },
              '20',
              { text: '_______', font: 'Roboto' },
              ` at Lot 41, Rolli Street, Kingston, Jamaica in the parish of Kingston and that the said`
            ]
          }
        ]
      },
      {
        marginBottom: bigMargin / 2,
        columns: [
          { text: '3.', width: 50 },
          {
            text: [
              `That the name of James Brow subscribing as a witness to the due execution of the said indenture is of the proper handwriting of me this Deponent and I am a `,
              { text: '_____________________________________', font: 'Roboto' },
              ` residing at Lot 41, Rolli Street, Kingston, Jamaica in the Parish of Kingston`
            ]
          }
        ]
      },
      {
        layout: tableBoxLayout,
        marginTop: bigMargin,
        marginBottom: bigMargin,
        table: {
          widths: ['*'],
          body: [
            [
              {
                columnGap: 10,
                fontSize: 10,
                columns: [
                  { text: '', width: 250 },
                  { text: ')', alignment: 'center', width: 'auto' },
                  ''
                ]
              }
            ],
            [
              {
                columnGap: 10,
                fontSize: 10,
                columns: [
                  {
                    text: 'SWORN to at Lot 41, Rolli Street, Kingston, Jamaica',
                    width: 250
                  },
                  { text: ')', alignment: 'center', width: 'auto' },
                  ''
                ]
              }
            ],
            [
              {
                columnGap: 10,
                fontSize: 10,
                columns: [
                  { text: '', width: 250 },
                  { text: ')', alignment: 'center', width: 'auto' },
                  ''
                ]
              }
            ],
            [
              {
                columnGap: 10,
                fontSize: 10,
                columns: [
                  { text: 'in the Parish of Kingston', width: 250 },
                  { text: ')', alignment: 'center', width: 'auto' },
                  ''
                ]
              }
            ],
            [
              {
                columnGap: 10,
                fontSize: 10,
                columns: [
                  { text: '', width: 250 },
                  { text: ')', alignment: 'center', width: 'auto' },
                  ''
                ]
              }
            ],
            [
              {
                columnGap: 10,
                fontSize: 10,
                columns: [
                  {
                    text: [
                      'this ',
                      { text: '_________', font: 'Roboto' },
                      ' day of ',
                      { text: '________________', font: 'Roboto' },
                      ' 20 ',
                      { text: '_____', font: 'Roboto' }
                    ],
                    width: 250
                  },
                  { text: ')', alignment: 'center', width: 'auto' },
                  ''
                ]
              }
            ],
            [
              {
                columnGap: 10,
                fontSize: 10,
                columns: [
                  { text: '', width: 250 },
                  { text: ')', alignment: 'center', width: 'auto' },
                  ''
                ]
              }
            ],
            [
              {
                columnGap: 10,
                fontSize: 10,
                columns: [
                  {
                    text: [
                      'Before me: ',
                      {
                        text: '___________________________________________',
                        font: 'Roboto'
                      }
                    ],
                    width: 250
                  },
                  { text: ')', alignment: 'center', width: 'auto' },
                  {
                    text: '_________________________________________________',
                    font: 'Roboto',
                    width: '*'
                  }
                ]
              }
            ],
            [
              {
                columnGap: 10,
                fontSize: 10,
                marginTop: -5,
                columns: [
                  {
                    text: 'NAME OF JUSTICE OF THE PEACE',
                    alignment: 'right',
                    width: 250
                  },
                  { text: ')', alignment: 'center', width: 'auto' },
                  {
                    text: 'Signature of WITNESS',
                    alignment: 'center',
                    width: '*'
                  }
                ]
              }
            ],
            [
              {
                columnGap: 10,
                fontSize: 10,
                columns: [
                  { text: '', width: 250 },
                  { text: ')', alignment: 'center', width: 'auto' },
                  ''
                ]
              }
            ],
            [
              {
                columnGap: 10,
                fontSize: 10,
                columns: [
                  { text: '', width: 250 },
                  { text: ')', alignment: 'center', width: 'auto' },
                  ''
                ]
              }
            ],
            [
              {
                columnGap: 10,
                fontSize: 10,
                columns: [
                  {
                    text: '_______________________________________________________',
                    width: 250,
                    font: 'Roboto'
                  },
                  { text: ')', alignment: 'center', width: 'auto' },
                  ''
                ]
              }
            ],
            [
              {
                marginTop: -8,
                fontSize: 10,
                columnGap: 10,
                columns: [
                  {
                    text: 'Signature of JUSTICE OF THE PEACE',
                    width: 250,
                    alignment: 'center'
                  },
                  { text: '', alignment: 'center', width: 'auto' },
                  ''
                ]
              }
            ]
          ]
        }
      }
    ],
    images: {
      companyImage: data.company?.companyImage
    },
    styles: {
      tableHeader: {
        bold: true
      },
      supSignature: {
        fontSize: 18,
        sup: true,
        alignment: 'center'
      },
      tableHeading: {
        alignment: 'left',
        fontSize: 16,
        bold: true
      }
    },
    defaultStyle: {
      font: 'Lora',
      color: '#111827',
      fontSize: 11,
      lineHeight: 1.3
    }
  };

  return dd;
};
